import activityResource, { ActivityTypes, ActivityDescriptions } from "../../api/activityResource";
import { getUser } from "../../utils/auth";

const state = () => ({
  activities: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
  },
});

const mutations = {
  SET_ACTIVITIES(state, activities) {
    state.activities = activities;
  },
  ADD_ACTIVITY(state, activity) {
    // Add to the beginning of the array (most recent first)
    state.activities.unshift(activity);
    // Keep only the last 100 activities in memory
    if (state.activities.length > 100) {
      state.activities.pop();
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  CLEAR_ACTIVITIES(state) {
    state.activities = [];
  },
};

const actions = {
  /**
   * Fetch activities from the API
   * @param {Object} context
   * @param {Object} params - Query parameters
   */
  async fetchActivities({ commit }, params = {}) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);

    try {
      const response = await activityResource.getAll(params);
      const activities = response.data.results || response.data || [];
      commit("SET_ACTIVITIES", activities);

      if (response.data.count) {
        commit("SET_PAGINATION", { total: response.data.count });
      }
    } catch (error) {
      console.error("Failed to fetch activities:", error);
      commit("SET_ERROR", "Failed to load activity log");
      // If API is not available, keep local activities
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Log a new activity
   * @param {Object} context
   * @param {Object} activityData - Activity data
   */
  async logActivity({ commit, rootState }, activityData) {
    const user = getUser() || {};
    const currentUser = rootState.user || {};

    // Build display name from available fields
    const firstName = user.first_name || currentUser.firstName || "";
    const lastName = user.last_name || currentUser.lastName || "";
    const displayName =
      firstName || lastName
        ? `${firstName} ${lastName}`.trim()
        : user.username ||
          currentUser.username ||
          user.email ||
          currentUser.email ||
          "Unknown User";

    const activity = {
      type: activityData.type,
      description:
        activityData.description || ActivityDescriptions[activityData.type] || activityData.type,
      userId: user.id || currentUser.id,
      userName: displayName,
      userEmail: user.email || currentUser.email,
      details: activityData.details || {},
      entityType: activityData.entityType || null,
      entityId: activityData.entityId || null,
      entityName: activityData.entityName || null,
      timestamp: new Date().toISOString(),
    };

    // Add to local store immediately for instant feedback
    commit("ADD_ACTIVITY", activity);

    // Try to persist to backend (non-blocking)
    try {
      await activityResource.log(activity);
    } catch (error) {
      // Log error but don't fail - activity is still tracked locally
      console.warn("Failed to persist activity to server:", error);
    }
  },

  /**
   * Log user login activity
   */
  // eslint-disable-next-line no-unused-vars
  logLogin({ dispatch }, userData) {
    return dispatch("logActivity", {
      type: ActivityTypes.USER_LOGIN,
      details: { loginTime: new Date().toISOString() },
    });
  },

  /**
   * Log user logout activity
   */
  logLogout({ dispatch }) {
    return dispatch("logActivity", {
      type: ActivityTypes.USER_LOGOUT,
      details: { logoutTime: new Date().toISOString() },
    });
  },

  /**
   * Log dashboard action
   */
  logDashboardAction({ dispatch }, { action, dashboard }) {
    const typeMap = {
      create: ActivityTypes.DASHBOARD_CREATED,
      update: ActivityTypes.DASHBOARD_UPDATED,
      delete: ActivityTypes.DASHBOARD_DELETED,
      view: ActivityTypes.DASHBOARD_VIEWED,
    };

    return dispatch("logActivity", {
      type: typeMap[action] || action,
      entityType: "dashboard",
      entityId: dashboard.id,
      entityName: dashboard.name || dashboard.title,
      details: { dashboardId: dashboard.id, dashboardName: dashboard.name },
    });
  },

  /**
   * Log user management action
   */
  logUserAction({ dispatch }, { action, targetUser }) {
    const typeMap = {
      create: ActivityTypes.USER_CREATED,
      update: ActivityTypes.USER_UPDATED,
      delete: ActivityTypes.USER_DELETED,
      invite: ActivityTypes.USER_INVITED,
      roleChange: ActivityTypes.USER_ROLE_CHANGED,
    };

    return dispatch("logActivity", {
      type: typeMap[action] || action,
      entityType: "user",
      entityId: targetUser.id,
      entityName: targetUser.username || targetUser.email,
      details: {
        targetUserId: targetUser.id,
        targetUserEmail: targetUser.email,
      },
    });
  },

  /**
   * Log data/file action
   */
  logDataAction({ dispatch }, { action, data }) {
    const typeMap = {
      upload: ActivityTypes.DATA_UPLOADED,
      push: ActivityTypes.DATA_PUSHED,
      delete: ActivityTypes.DATA_DELETED,
      update: ActivityTypes.DATA_UPDATED,
      fileUpload: ActivityTypes.FILE_UPLOADED,
      fileDelete: ActivityTypes.FILE_DELETED,
    };

    return dispatch("logActivity", {
      type: typeMap[action] || action,
      entityType: "data",
      entityId: data.id,
      entityName: data.name || data.filename,
      details: { dataId: data.id, fileName: data.filename },
    });
  },

  /**
   * Log role action
   */
  logRoleAction({ dispatch }, { action, role }) {
    const typeMap = {
      create: ActivityTypes.ROLE_CREATED,
      update: ActivityTypes.ROLE_UPDATED,
      delete: ActivityTypes.ROLE_DELETED,
    };

    return dispatch("logActivity", {
      type: typeMap[action] || action,
      entityType: "role",
      entityId: role.id,
      entityName: role.name,
      details: { roleId: role.id, roleName: role.name },
    });
  },

  /**
   * Clear all local activities
   */
  clearActivities({ commit }) {
    commit("CLEAR_ACTIVITIES");
  },
};

const getters = {
  activities: (state) => state.activities,
  loading: (state) => state.loading,
  error: (state) => state.error,
  pagination: (state) => state.pagination,
  recentActivities: (state) => state.activities.slice(0, 10),
  activitiesByType: (state) => (type) => state.activities.filter((a) => a.type === type),
  activitiesByUser: (state) => (userId) => state.activities.filter((a) => a.userId === userId),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
